import axios from 'axios';

import { NPT_API_URL } from '@config';
import { useAuthStore } from '@core/features/auth';
import i18n from '@i18n/i18n';
import { isValidToken } from '@utils';

import { client } from './generated/client.gen';
import * as services from './generated/sdk.gen';

export type TokenPair = {
  access: string;
  refresh: string;
};

/**
 * Refresh access token using the refresh token.
 * Uses a separate axios instance to avoid interceptor loops.
 */
export async function refreshAccessToken(): Promise<TokenPair | undefined> {
  const refreshToken = useAuthStore.getState().refreshToken;

  // If the refreshToken is not valid, logout the user by deleting the tokens.
  if (!refreshToken || !isValidToken(refreshToken)) {
    useAuthStore.getState().deleteTokens();
    return;
  }

  try {
    const baseURL = client.getConfig().baseURL ?? '';
    const response = await axios.post(
      `${baseURL}/auth/token/refresh/`,
      { refresh: JSON.parse(refreshToken) },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const newAccessToken = response.data.access;
    const newRefreshToken = response.data.refresh || refreshToken;

    if (newAccessToken) {
      useAuthStore.getState().setTokens({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
      });
      return { access: newAccessToken, refresh: newRefreshToken };
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
  }

  // Refresh failed - logout
  console.log('DELETE TOKENS - refreshAccessToken failed');
  useAuthStore.getState().deleteTokens();
  return;
}

/**
 * Configure and return the API client with auth interceptors
 */
export const createApiClient = (baseURL?: string) => {
  // Configure the client
  client.setConfig({
    baseURL: baseURL ?? NPT_API_URL,
  });

  // Request interceptor
  client.instance.interceptors.request.use((config) => {
    const accessToken = useAuthStore.getState().accessToken;

    // Add Authorization header if token exists
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add Accept-Language header
    config.headers['Accept-Language'] = i18n.language;

    return config;
  });

  // Response interceptor
  client.instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 errors with token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const tokens = await refreshAccessToken();

          if (!tokens) {
            // Refresh failed, reject the error
            return Promise.reject(error);
          }

          // Update header and retry
          originalRequest.headers.Authorization = `Bearer ${tokens.access}`;
          const response = await client.instance(originalRequest);

          if (response.status !== 200) {
            console.log('DELETE TOKENS - retry failed');
            useAuthStore.getState().deleteTokens();
            client.instance.defaults.headers['Authorization'] = '';
            window.location.reload();
          }

          return response;
        } catch (retryError) {
          // If token refresh fails, logout the user
          console.log('DELETE TOKENS - errorInterceptor error', retryError);
          useAuthStore.getState().deleteTokens();
          client.instance.defaults.headers['Authorization'] = '';
          window.location.reload();
          return Promise.reject(retryError);
        }
      }

      return Promise.reject(error);
    }
  );

  return services;
};

/**
 * Type for the configured API client
 */
export type ApiClient = {
  [K in keyof typeof services]: (typeof services)[K];
};

/**
 * Set the Accept-Language header for internationalization
 */
export const setLanguageHeader = (language: string): void => {
  client.instance.defaults.headers['Accept-Language'] = language;
};

// Re-export types
export type * from './generated/types.gen';
